import { KgInsight } from '@api';
import { Button } from '@design';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsInsightEmptyState } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-empty-state/EarningsInsightEmptyState';
import { EarningsInsightTabLines } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/EarningsInsightTabLines';
import { useTranslation } from 'react-i18next';
import { CatalystWidgetView } from '../../CatalystWidgetView';
import { useEarningsKgInsightTab } from '../../hooks/useEarningsKgInsightTab';
import { KgInsightPreview } from '../kg-insight-content/KgInsightPreview';
import { KgInsightSkeleton } from '../kg-insight-content/KgInsightSkeleton';
import { TopTakeaways } from '../top-takeaways/TopTakeaways';

interface EarningsKgInsightWidgetProps {
  navigateToKGInsight: () => void;
  kgInsight: KgInsight;
}

export const EarningsKgInsightWidget = ({
  navigateToKGInsight,
  kgInsight,
}: EarningsKgInsightWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const { params } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const { activeInsight, emptyStateVariant, showLoading } =
    useEarningsKgInsightTab(kgInsight);

  if (showLoading) {
    return <KgInsightSkeleton />;
  }

  return (
    <CatalystWidgetView
      subject={kgInsight.subject}
      tabsSlot={<EarningsInsightTabLines />}
    >
      {activeInsight ? (
        <KgInsightPreview
          kgInsight={activeInsight}
          hideCatalystBadge={isExpanded}
          keyStatsItemsPerRow={3}
        >
          <TopTakeaways
            kgInsight={activeInsight}
            source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
          />
          <Button
            onClick={navigateToKGInsight}
            fullWidth
            label={t('insights:catalystGeneration.readFullCatalyst')}
            data-testid="read-full-catalyst"
          />
        </KgInsightPreview>
      ) : (
        <EarningsInsightEmptyState variant={emptyStateVariant} />
      )}
    </CatalystWidgetView>
  );
};
